import React, { useContext, useMemo, useState } from "react"
import { observer } from "mobx-react"
import { Spotlight, type SpotlightActionData } from "@mantine/spotlight"
import { IconBook, IconCode, IconHome, IconRocket, IconSearch } from "@tabler/icons-react"
import { AppWorkspaceContext } from "../stores/AppWorkspace"
import { FRAMEWORKS, frameworkHeading } from "../../docs/catalog"
import { searchChunk } from "../../docs/lazy"
import { captureFrameworkSelect } from "../../analytics"

export const DocsSpotlight: React.FC = observer(() => {
  const workspace = useContext(AppWorkspaceContext)
  const [query, setQuery] = useState("")
  // The index is its own chunk, fetched when the spotlight opens. Read here,
  // not inside the memo, so this observer re-renders when it lands.
  const search = query.trim() ? searchChunk.current : null

  const actions = useMemo<SpotlightActionData[]>(() => {
    if (!query.trim()) {
      return [
        {
          id: "home",
          label: "Home",
          description: "What OpenReceive is, and the live demo",
          leftSection: <IconHome size={18} />,
          onClick: () => workspace.goHome()
        },
        ...FRAMEWORKS.map((framework) => ({
          id: `framework-${framework.id}`,
          label: framework.label,
          description: frameworkHeading(framework),
          leftSection: <IconRocket size={18} />,
          onClick: () => {
            captureFrameworkSelect(framework.id, "docs_search")
            workspace.goIntegration(framework.id)
          }
        })),
        {
          id: "api",
          label: "API docs",
          description: "Functions, HTTP routes, CLI, Rails",
          leftSection: <IconCode size={18} />,
          onClick: () => workspace.goApiDocs()
        },
        {
          id: "guides",
          label: "Guides",
          description: "Platform guides and recipes",
          leftSection: <IconBook size={18} />,
          onClick: () => workspace.goGuides()
        }
      ]
    }

    return (search?.searchDocs(query, { limit: 18 }) ?? []).map((hit) => ({
      id: hit.id,
      label: hit.title,
      description: hit.kind === "api" ? `${hit.platform} · ${hit.section}` : hit.excerpt,
      leftSection: hit.kind === "api" ? <IconCode size={18} /> : <IconBook size={18} />,
      onClick: () => workspace.openHref(hit.href)
    }))
  }, [query, search, workspace])

  return (
    <Spotlight
      shortcut={["mod + K"]}
      actions={actions}
      nothingFound={query.trim() && !search ? "Loading search…" : "No matching docs"}
      highlightQuery
      searchProps={{
        leftSection: <IconSearch size={18} />,
        placeholder: "Search API docs and guides…"
      }}
      onQueryChange={setQuery}
      onSpotlightOpen={() => void searchChunk.load().catch(() => undefined)}
      query={query}
    />
  )
})
